import { ACHIEVEMENTS } from "./achievements.js";
import { displayReadingText } from "./letter-display.js";
import { isSoundMasteryLevel, levelUpGate, profileStats, workingLevel } from "./algorithm.js";
import { READING_BANDS } from "./bands.js";
import { corpus, corpusById } from "./corpus.js";
import { shopCoinsSpent } from "./shop-items.js";
import { LETTER_GONE_LEVEL, MAX_LEVEL, Profile, ReadingText, ResultKind, Subject } from "./type.app.js";

export type SubjectSummary = {
  subject: Subject;
  label: string;
  level: number;
  participated: boolean;
  answers: number;
  right: number;
  wrong: number;
  skipped: number;
  easy: number;
};

export const subjectSummary = (profile: Profile, subject: Subject): SubjectSummary => {
  if (subject === "reading") {
    const events = profile.events;
    return {
      subject,
      label: "Reading",
      level: profile.subjectProgress?.reading.level ?? profile.level,
      participated: events.length > 0,
      answers: events.length,
      right: events.filter((event) => event.result === "right").length,
      wrong: events.filter((event) => event.result === "wrong").length,
      skipped: events.filter((event) => event.result === "skip").length,
      easy: events.filter((event) => event.result === "wayTooEasy").length,
    };
  }
  const progress = profile.subjectProgress?.[subject];
  const events = progress?.events ?? [];
  return {
    subject,
    label: subject === "math" ? "Math" : "Spelling",
    level: progress?.level ?? 1,
    participated: events.length > 0,
    answers: events.length,
    right: events.filter((event) => event.result === "right").length,
    wrong: events.filter((event) => event.result === "wrong").length,
    skipped: events.filter((event) => event.result === "skip").length,
    easy: events.filter((event) => event.result === "wayTooEasy").length,
  };
};

export type TeacherMeter = {
  label: string;
  value: number;
  max: number;
};

export type TeacherRecent = {
  result: ResultKind;
  text: string;
  at: string;
};

export type TeacherSnapshot = {
  name: string;
  bandLabel: string;
  level: number;
  workingLevel: number;
  nextLevel: number;
  remaining: number;
  progressPercent: number;
  boostActive: boolean;
  atMaxLevel: boolean;
  reviewMode: boolean;
  wrongStreak: number;
  meters: TeacherMeter[];
  headline: string;
  detail: string;
  note: string;
  figure: string;
  tags: string[];
  stats: { read: number; right: number; wrong: number; skip: number; wayTooEasy: number; accuracy: string };
  mastered: number;
  currentText: string | null;
  lastActiveAt: string | null;
  createdAt: string;
  readsToday: number;
  readsWeek: number;
  coins: number;
  coinsEarned: number;
  peakCoins: number;
  spent: number;
  items: number;
  achievements: number;
  achievementTotal: number;
  maxCorrectStreak: number;
  recent: TeacherRecent[];
  isCurrent: boolean;
};

const bandLabel = (band: Profile["band"]): string => {
  return READING_BANDS.find((item) => item.id === band)?.label ?? band;
};

const dayStart = (now: Date): number => {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  return start.getTime();
};

const countSince = (profile: Profile, since: number): number => {
  return profile.events.filter((event) => Date.parse(event.at) >= since).length;
};

export const teacherSnapshot = (
  profile: Profile,
  currentProfileId: string | null = null,
  texts: ReadingText[] = corpus,
  now = new Date(),
): TeacherSnapshot => {
  const stats = profileStats(profile);
  const scored = stats.right + stats.wrong;
  const work = workingLevel(profile);
  const soundLevel = isSoundMasteryLevel(work);
  const gate = levelUpGate(profile, texts);
  const needed = gate.streakNeeded;
  const streak = gate.streak;
  const atMaxLevel = profile.level >= MAX_LEVEL && !profile.boostActive;
  const nextLevel = Math.min(MAX_LEVEL, work + 1);
  const soundGate = work < LETTER_GONE_LEVEL && gate.soundsNeeded > 0;
  const meters: TeacherMeter[] = [];
  if (!soundLevel) {
    meters.push({ label: "Right in a row", value: streak, max: needed });
    meters.push(
      { label: "Reads at this level", value: Math.min(gate.seen, gate.seenNeeded), max: gate.seenNeeded },
      {
        label: `Correct of last ${gate.recentWindow}`,
        value: Math.min(gate.recentCorrect, gate.recentNeeded),
        max: gate.recentNeeded,
      },
    );
  }
  if (soundGate) {
    meters.push({
      label: "Sounds with 5 in a row",
      value: gate.soundsMastered,
      max: Math.max(1, gate.soundsNeeded),
    });
  }

  const seenLeft = Math.max(0, gate.seenNeeded - gate.seen);
  const recentLeft =
    gate.recentCount < gate.recentWindow
      ? Math.max(gate.recentWindow - gate.recentCount, gate.recentNeeded - gate.recentCorrect)
      : Math.max(0, gate.recentNeeded - gate.recentCorrect);
  const streakLeft = Math.max(0, needed - streak);
  const soundsLeft = Math.max(0, gate.soundsNeeded - gate.soundsMastered);

  let headline = "";
  let detail = "";
  let note = "";
  let remaining = 0;
  let progressPercent = 0;
  if (atMaxLevel) {
    headline = "Highest level reached";
    detail = `${profile.name} is at the top of the reading path.`;
    note = "There is no further automatic level-up from here.";
    remaining = 0;
    progressPercent = 100;
  } else if (soundGate && !gate.coverageOk) {
    remaining = soundsLeft;
    headline = remaining === 1 ? "1 sound still needs 5 in a row" : `${remaining} sounds still need 5 in a row`;
    detail = `${gate.soundsMastered} of ${gate.soundsNeeded} letters and letter combinations have an active 5-right streak.`;
    note =
      "Previous letters stay in the mix. The level will not go up until every introduced sound is correct 5 times in a row.";
    progressPercent = Math.min(100, Math.round((gate.soundsMastered / Math.max(1, gate.soundsNeeded)) * 100));
  } else if (gate.ready) {
    headline = "Ready to level up";
    detail = soundLevel
      ? `Every introduced sound has 5 correct in a row.`
      : `${streak} of ${needed} in a row, ${gate.seen} reads at this level, and ${gate.recentCorrect} of the last ${gate.recentCount} correct.`;
    note = profile.boostActive
      ? `A boost is active, so right answers count toward working level ${work} (badge ${profile.level}).`
      : "The gold badge never goes down. After misses, easier mastered text can appear for support.";
    remaining = 0;
    progressPercent = 100;
  } else if (!soundLevel && seenLeft > 0) {
    remaining = seenLeft;
    headline = remaining === 1 ? "1 more read at this level" : `${remaining} more reads at this level`;
    detail = `${gate.seen} of ${gate.seenNeeded} reads at level ${work}.`;
    note =
      "Only prompts from this exact level count. Nearby review and preview do not. Level-up also needs 10 right in a row and 45 of the last 50 correct.";
    progressPercent = Math.min(100, Math.round((gate.seen / gate.seenNeeded) * 100));
  } else if (!soundLevel && recentLeft > 0) {
    remaining = recentLeft;
    headline =
      gate.recentCount < gate.recentWindow
        ? `Need ${gate.recentNeeded} of the last ${gate.recentWindow} correct`
        : `${remaining} more correct in the last ${gate.recentWindow}`;
    detail = `${gate.recentCorrect} of the last ${gate.recentCount} scored answers at this level were correct.`;
    note = "Skip does not count. Right and way too easy both count as correct.";
    progressPercent = Math.min(100, Math.round((gate.recentCorrect / gate.recentNeeded) * 100));
  } else {
    remaining = streakLeft;
    headline =
      remaining === 0
        ? "Ready to level up"
        : remaining === 1
          ? "1 more right in a row"
          : `${remaining} more right in a row`;
    detail = `${streak} of ${needed} consecutive correct answers toward level ${nextLevel}.`;
    note = profile.boostActive
      ? `A boost is active, so right answers count toward working level ${work} (badge ${profile.level}).`
      : "The gold badge never goes down. After misses, easier mastered text can appear for support.";
    progressPercent = Math.min(100, Math.round((streak / needed) * 100));
  }

  const last = profile.events[profile.events.length - 1];
  const today = dayStart(now);
  const week = today - 6 * 24 * 60 * 60 * 1000;
  const current = profile.currentTextId ? (corpusById[profile.currentTextId] ?? null) : null;
  const spent = shopCoinsSpent(profile.inventory);
  const isCurrent = profile.id === currentProfileId;
  const tags: string[] = [];
  if (isCurrent) tags.push("Reading now");
  if (profile.wrongStreak > 0) tags.push("Support mix");
  if (profile.boostActive) tags.push("Boost");
  if (atMaxLevel) tags.push("Highest level");

  const figure = atMaxLevel ? "Max" : remaining === 0 ? "Ready" : String(remaining);

  return {
    name: profile.name,
    bandLabel: bandLabel(profile.band),
    level: profile.level,
    workingLevel: work,
    nextLevel,
    remaining,
    progressPercent,
    boostActive: profile.boostActive,
    atMaxLevel,
    reviewMode: profile.wrongStreak > 0,
    wrongStreak: profile.wrongStreak,
    meters,
    headline,
    detail,
    note,
    figure,
    tags,
    stats: {
      ...stats,
      accuracy: scored === 0 ? "—" : `${Math.round((stats.right / scored) * 100)}%`,
    },
    mastered: Object.values(profile.textStats).filter((stat) => stat.retired).length,
    currentText: current ? displayReadingText(current) : null,
    lastActiveAt: last?.at ?? null,
    createdAt: profile.createdAt,
    readsToday: countSince(profile, today),
    readsWeek: countSince(profile, week),
    coins: profile.coins,
    coinsEarned: profile.coinsEarned,
    peakCoins: profile.peakCoins,
    spent,
    items: profile.inventory.length,
    achievements: profile.achievements.length,
    achievementTotal: ACHIEVEMENTS.length,
    maxCorrectStreak: profile.maxCorrectStreak,
    recent: profile.events
      .slice(-8)
      .reverse()
      .map((event) => ({
        result: event.result,
        text: event.text,
        at: event.at,
      })),
    isCurrent,
  };
};

export const formatTeacherDate = (iso: string, now = new Date()): string => {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return "Unknown";
  const delta = now.getTime() - then;
  if (delta < 45_000) return "Just now";
  if (delta < 3_600_000) return `${Math.max(1, Math.round(delta / 60_000))} min ago`;
  if (delta < 22 * 3_600_000) return `${Math.max(1, Math.round(delta / 3_600_000))} hr ago`;
  return new Date(then).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

export const recentLabel = (result: ResultKind): string => {
  if (result === "right") return "Right";
  if (result === "wrong") return "Practice";
  if (result === "skip") return "Skip";
  return "Easy";
};
